import { bacteriaMetaConfig_9P } from '../calculate_ctvalue';
import { getBacteriaScore } from '../../utils/bacteriaEvaluator';

// =====================================================================
// Config_macronutrient.js  —  9P panel "macronutrient" summary section
// ---------------------------------------------------------------------
// Flow:
//   1. For each species below, the ct value from the imported CSV is
//      converted to a "scroll" (score) using macronutrient_9P_scoreMapping.
//      -> EDIT the thresholds there yourself (e.g. ct 36 => score 0).
//   2. All species scores are summed (X).
//   3. X is compared against the conditions to get the result label:
//         X <= 2.3            -> Slightly above optimum
//         2.3 < X <= 3.25     -> Slightly below optimum
//         X > 3.25            -> Optimum
//   4. The result is shown on page 19 (eng) / page 20 (thai).
// =====================================================================

// Species that are summed for this section.
// NOTE: these keys must match the CSV column / formData keys exactly.
const macronutrient_9P_sp = [
  'Bifidobacterium bifidum',
  'Bifidobacterium breve',
  'Bifidobacterium lactis',
  'Bifidobacterium longum',
  'Bacteroides genus', // "bacteroides"
];

// ---------------------------------------------------------------------
// Per-species LABEL -> score ("scroll") mapping.
// The ct value is first evaluated by calculate_ctvalue.js
// (bacteriaMetaConfig_9P) which returns a label:
//     'Deficient' | 'Slightly Deficient' | 'Normal'
// Here you just decide how many points each label is worth per species.
// >>> THESE ARE EXAMPLE VALUES. Tune the score per label / per species. <<<
// ---------------------------------------------------------------------
const macronutrient_9P_scoreMapping = {
  'Bifidobacterium bifidum': {
    'Deficient': 0.125,
    'Slightly Deficient': 0.25,
    'Normal': 0.5,
  },
  'Bifidobacterium breve': {
    'Deficient': 0.125,
    'Slightly Deficient': 0.25,
    'Normal': 0.5,
  },
  'Bifidobacterium lactis': {
    'Deficient': 0.125,
    'Slightly Deficient': 0.25,
    'Normal': 0.5,
  },
  'Bifidobacterium longum': {
    'Deficient': 0.25,
    'Slightly Deficient': 0.5,
    'Normal': 1,
  },
  'Bacteroides genus': {
    'Deficient': 0.375,
    'Slightly Deficient': 0.75,
    'Normal': 1.5,
  },
};

// ---------------------------------------------------------------------
// Result conditions + on-page position — ENGLISH version (page 19).
// X = summed score across all species above.
// Adjust the Xpos/Ypos/image values to match your PDF layout.
// ---------------------------------------------------------------------
const macronutrient_9P_condition = {
  page: 19, // eng version page
  XposText: 505,
  YposText: 386,
  scale_imageX: 67,
  scale_imageY: 67,
  XposImage: 88,
  YposImage: 350,
  // Bar (color) positioning — 5 bars for visual; arrow lands on matched score (1-3).
  xBarStart: 174,
  YBarStart: 394,
  xBarGap: 64,
  conditions: [
    {
      condition: (x) => x <= 2.3,
      score: 4,
      label: 'Slightly above optimum',
      image: 'Indigestible3',
    },
    {
      condition: (x) => x > 2.3 && x <= 3.25,
      score: 2,
      label: 'Slightly below optimum',
      image: 'Indigestible1',
    },
    {
      condition: (x) => x > 3.25,
      score: 3,
      label: 'Optimum',
      image: 'Indigestible2',
    },
  ],
};

// ---------------------------------------------------------------------
// Result conditions + on-page position — THAI version (page 20).
// Same thresholds, Thai labels + Thai-page positions.
// ---------------------------------------------------------------------
const macronutrient_9P_condition_thai = {
  page: 18, // thai version page
  XposText: 510,
  YposText: 385,
  scale_imageX: 67,
  scale_imageY: 67,
  XposImage: 87,
  YposImage: 348,
  // Bar (color) positioning — 5 bars for visual; arrow lands on matched score (1-3).
  xBarStart: 175,
  YBarStart: 395,
  xBarGap: 64,
  conditions: [
    {
      condition: (x) => x <= 2.3,
      score: 4,
      label: 'สูงกว่าระดับที่เหมาะสมเล็กน้อย',
      image: 'Indigestible3',
    },
    {
      condition: (x) => x > 2.3 && x <= 3.25,
      score: 2,
      label: 'ต่ำกว่าระดับที่เหมาะสมเล็กน้อย',
      image: 'Indigestible1',
    },
    {
      condition: (x) => x > 3.25,
      score: 3,
      label: 'เหมาะสม',
      image: 'Indigestible2',
    },
  ],
};

// ---------------------------------------------------------------------
// Helper: score ("scroll") for one species.
// 1. Evaluate the ct value via calculate_ctvalue.js -> label
//    ('Deficient' | 'Slightly Deficient' | 'Normal').
// 2. Convert that label to a score using macronutrient_9P_scoreMapping.
// Returns 0 when the species/label is unknown or the value is missing.
// ---------------------------------------------------------------------
function getMacronutrient9PSpeciesScore(species, formData) {
  const raw = parseFloat(formData?.[species]);
  if (isNaN(raw)) return 0;

  const meta = bacteriaMetaConfig_9P[species];
  if (!meta) return 0;

  // label computed by the same logic as the rest of the 9P report
  const { label } = getBacteriaScore(species, raw, formData, meta);

  const mapping = macronutrient_9P_scoreMapping[species];
  if (!mapping || mapping[label] === undefined) return 0;

  return mapping[label];
}

// ---------------------------------------------------------------------
// Main: sum all species scores and resolve the result label.
// Pass useThaiVersion=true to use the Thai config (page 20).
// Returns: { sum, label, image, score, config }
// ---------------------------------------------------------------------
function calculateMacronutrient9P(formData, useThaiVersion = false) {
  let sum = 0;

  macronutrient_9P_sp.forEach((species) => {
    sum += getMacronutrient9PSpeciesScore(species, formData);
  });

  // round to 2 decimals to avoid float noise (e.g. 0.1 + 0.2)
  sum = parseFloat(sum.toFixed(2));

  const config = useThaiVersion
    ? macronutrient_9P_condition_thai
    : macronutrient_9P_condition;

  const matched = config.conditions.find((c) => c.condition(sum));

  return {
    sum,
    label: matched ? matched.label : null,
    image: matched ? matched.image : null,
    score: matched ? matched.score : null,
    config,
  };
}

export {
  macronutrient_9P_sp,
  macronutrient_9P_scoreMapping,
  macronutrient_9P_condition,
  macronutrient_9P_condition_thai,
  getMacronutrient9PSpeciesScore,
  calculateMacronutrient9P,
};
